import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { cn, useKeyboardInset } from '@shared/lib';
import { CheckIcon, PlainField, PlusIcon } from '@shared/ui';
import type { ModuleSummary } from '@shared/types';
import { buildModuleMenu, moduleLetter } from '../lib/composerModel';
import { useVisibleHeight } from '../model/usePreviewRoom';

interface ModuleMenuProps {
  id: string;
  /** popup — компьютер: попап вверх от чипа; sheet — телефон: лист снизу порталом в body. */
  variant?: 'popup' | 'sheet';
  /** Свежий список модулей; undefined — ещё грузится (скелетон строк). */
  modules: ModuleSummary[] | undefined;
  recentIds: readonly string[];
  currentId: string | null;
  /** Чип: клик по нему меню закрывает он сам, а не «клик вне» (только попап). */
  anchorRef?: RefObject<HTMLElement>;
  onSelect: (module: ModuleSummary, fromRecent: boolean) => void;
  onCreate: () => void;
  /** `restoreFocus` — вернуть фокус на чип (Esc, выбор); клик вне — нет. */
  onClose: (restoreFocus: boolean) => void;
}

/**
 * Меню модулей (ТЗ главной § 9). Компьютер — попап вверх от чипа, над строкой и листом; лист снизу —
 * телефон: порталом в body (z-90, своё затемнение z-89), заголовок «Куда добавить карточки», над
 * клавиатурой, если она открыта; это слой истории (§ 7). Поиск — если модулей больше 6 (на компьютере
 * фокус сразу в него, на телефоне — по касанию), «Недавние» — до трёх сверху, затем «Все модули» в
 * порядке сервера, внизу прилипает «+ Новый модуль». ↑ ↓ и Enter работают; Esc закрывает только меню.
 */
export function ModuleMenu({
  id,
  variant = 'popup',
  modules,
  recentIds,
  currentId,
  anchorRef,
  onSelect,
  onCreate,
  onClose,
}: ModuleMenuProps) {
  const { t } = useTranslation();
  const sheet = variant === 'sheet';
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;
  const model = useMemo(() => (modules ? buildModuleMenu(modules, recentIds, query) : null), [modules, recentIds, query]);
  const ready = model !== null;
  const withSearch = model?.search ?? false;
  const inset = useKeyboardInset();
  const visible = useVisibleHeight(sheet);

  // Фокус: на компьютере — в поиск, если он есть, иначе на текущий модуль, чтобы ↑ ↓ сразу работали.
  // На телефоне — на сам лист: программный фокус в поиск открыл бы клавиатуру без спроса.
  useEffect(() => {
    if (!ready) return;
    const root = rootRef.current;
    if (sheet) {
      root?.focus({ preventScroll: true });
      return;
    }
    if (withSearch) {
      searchRef.current?.focus();
      return;
    }
    const target =
      root?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ??
      root?.querySelector<HTMLElement>('[data-menu-option]') ??
      root;
    target?.focus({ preventScroll: true });
  }, [ready, withSearch, sheet]);

  // Клик вне меню и чипа закрывает попап (лист под ним остаётся). У листа телефона — своё затемнение.
  useEffect(() => {
    if (sheet) return;
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || rootRef.current?.contains(target) || anchorRef?.current?.contains(target)) return;
      close.current(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [anchorRef, sheet]);

  const options = () => Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-menu-option]') ?? []);
  const move = (step: 1 | -1) => {
    const list = options();
    if (!list.length) return;
    const at = list.indexOf(document.activeElement as HTMLElement);
    const next = at < 0 ? (step > 0 ? 0 : list.length - 1) : (at + step + list.length) % list.length;
    list[next].focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      // Только меню: лист словаря под ним не сворачиваем.
      event.preventDefault();
      event.stopPropagation();
      close.current(true);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    }
  };

  const first = model ? (model.recent[0] ?? model.all[0]) : undefined;

  const option = (module: ModuleSummary, fromRecent: boolean) => {
    const selected = module.id === currentId;
    return (
      <button
        key={module.id}
        type="button"
        role="option"
        aria-selected={selected}
        data-menu-option
        onClick={() => onSelect(module, fromRecent)}
        // Имя модуля — целиком, до двух строк: многоточие только у чипа (ТЗ § 6, приёмка A1-02). Строка от 44 px
        // растёт с именем; буква, число и ✓ стоят по первой строке (у имени и числа строка 20 px и отступ 4 px —
        // середина 22 px, как у буквы 28 px при полях строки 8 px). Отступ — margin: в поле padding у line-clamp
        // выглядывал бы верх третьей строки.
        className="flex min-h-11 w-full flex-none items-start gap-3 rounded-[10px] px-2.5 py-2 text-left outline-none transition-colors focus-visible:bg-elev active:bg-elev [@media(hover:hover)]:hover:bg-elev"
      >
        <span
          aria-hidden
          className="grid h-7 w-7 flex-none place-items-center rounded-[8px] bg-elev font-serif text-[14px] font-semibold text-fg"
        >
          {moduleLetter(module.title)}
        </span>
        <span className="my-1 line-clamp-2 min-w-0 flex-1 break-words text-[14px] font-medium leading-5 text-fg">
          {module.title}
        </span>
        <span className="my-1 flex-none whitespace-nowrap text-[14px] leading-5 text-muted">
          {t('training.cardCount', { count: module.words })}
        </span>
        {selected ? (
          <CheckIcon className="mt-1.5 h-4 w-4 flex-none text-accent" aria-hidden />
        ) : (
          <span aria-hidden className="w-4 flex-none" />
        )}
      </button>
    );
  };

  const groupTitle = (label: string) => (
    <div className="px-2.5 pb-1 pt-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-muted">{label}</div>
  );

  const search = withSearch && (
    <div className="flex-none border-b border-solid border-line p-2">
      <PlainField
        ref={searchRef}
        value={query}
        onChange={setQuery}
        placeholder={t('homeComposer.module.search')}
        ariaLabel={t('homeComposer.module.search')}
        maxLength={60}
        enterKeyHint="go"
        onKeyDown={(event) => {
          if (event.key === 'Enter' && first) onSelect(first, model?.recent.includes(first) ?? false);
        }}
        // Шрифт поля 16 px на телефоне и на любом сенсорном экране (iPad от 901 px — попап компьютера):
        // мельче iOS увеличивает страницу при фокусе, а глобальное правило 16 px на contenteditable не
        // действует. Названия модулей — не в записях сессий и не в аналитике (ТЗ § 16): все три класса.
        className={cn(
          'hc-menu-search amp-mask ym-hide-content ym-disable-keys w-full rounded-[10px] border border-solid border-line bg-elev px-3 text-fg outline-none focus:border-[#3a3a40]',
          sheet ? 'h-11 text-[16px] leading-[44px]' : 'h-10 text-[14px] leading-10 [@media(pointer:coarse)]:text-[16px]',
        )}
      />
    </div>
  );

  const list = (
    <div role="listbox" aria-labelledby={titleId} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5">
      {!model ? (
        <div aria-hidden className="flex flex-col gap-1.5 p-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-9 animate-skeleton rounded-[10px] bg-elev" />
          ))}
        </div>
      ) : model.empty ? (
        <p className="px-2.5 py-3 text-[14px] text-muted">{t('homeComposer.module.empty', { q: query.trim() })}</p>
      ) : (
        <>
          {model.recent.length > 0 && model.headers && groupTitle(t('homeComposer.module.recent'))}
          {model.recent.map((m) => option(m, true))}
          {model.all.length > 0 && model.headers && groupTitle(t('homeComposer.module.all'))}
          {model.all.map((m) => option(m, false))}
        </>
      )}
    </div>
  );

  const createButton = (
    <button
      type="button"
      data-menu-option
      onClick={onCreate}
      className="flex h-11 flex-none items-center gap-2 border-t border-solid border-line px-4 text-left text-[14px] font-medium text-fg outline-none transition-colors focus-visible:bg-elev active:bg-elev [@media(hover:hover)]:hover:bg-elev"
    >
      <PlusIcon className="h-4 w-4 text-accent" aria-hidden />
      {t('homeComposer.module.new')}
    </button>
  );

  if (sheet) {
    return createPortal(
      <>
        <motion.div
          aria-hidden
          className="fixed inset-0 z-[89] bg-black/55"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={() => close.current(false)}
        />
        <motion.div
          ref={rootRef}
          id={id}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.26, ease: [0.2, 0.9, 0.2, 1] }}
          // Над клавиатурой iPhone (поиск по касанию): лист поднимается на её высоту; без клавиатуры —
          // отступ под safe-area. Не выше видимой области минус 48 px.
          style={{
            bottom: inset,
            maxHeight: Math.max(240, Math.min(560, visible - 48)),
            paddingBottom: inset ? 0 : 'env(safe-area-inset-bottom)',
          }}
          className="fixed inset-x-0 z-[90] mx-auto flex w-full max-w-[640px] flex-col overflow-hidden rounded-t-[22px] border border-b-0 border-solid border-line bg-[#1a1a1d] shadow-[0_-18px_40px_-12px_rgba(0,0,0,0.6)] outline-none"
        >
          <div className="flex min-h-14 flex-none items-center gap-2 pl-4 pr-1.5">
            <h2 id={titleId} className="min-w-0 flex-1 truncate font-serif text-[18px] font-medium text-fg">
              {t('homeComposer.module.title')}
            </h2>
            <button
              type="button"
              onClick={() => close.current(false)}
              aria-label={t('cardsWorkspace.aria.close')}
              className="grid h-11 w-11 flex-none place-items-center rounded-full text-muted transition-colors active:bg-elev"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="h-4 w-4" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          {search}
          {list}
          {createButton}
        </motion.div>
      </>,
      document.body,
    );
  }

  return (
    <div
      ref={rootRef}
      id={id}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="absolute bottom-[calc(100%+8px)] left-2 z-10 flex max-h-[360px] w-[340px] max-w-[calc(100vw-32px)] animate-cm-dropdown-in flex-col overflow-hidden rounded-2xl border border-solid border-line bg-[#1a1a1d] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)] outline-none motion-reduce:animate-none"
    >
      <h2 id={titleId} className="sr-only">
        {t('homeComposer.module.title')}
      </h2>
      {search}
      {list}
      {createButton}
    </div>
  );
}
